/**
 * Health Dashboard page — Admin only.
 *
 * Status grid with Green/Yellow/Red indicators per process.
 * Process detail panels with last run info, next scheduled run,
 * records processed, errors. 30-day timeline. Run Now buttons.
 *
 * Requirements: 52.1–52.9
 */

import { useState, useEffect, useCallback } from 'react';
import {
  adminService,
  ProcessHealthStatus,
  ProcessHealthDetail,
} from '../../services/adminService';

const STATUS_COLORS: Record<string, { bg: string; text: string; dot: string }> = {
  Green: { bg: 'bg-green-50', text: 'text-green-700', dot: 'bg-green-500' },
  Yellow: { bg: 'bg-yellow-50', text: 'text-yellow-700', dot: 'bg-yellow-500' },
  Red: { bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500' },
};

export function HealthDashboard() {
  const [processes, setProcesses] = useState<ProcessHealthStatus[]>([]);
  const [selectedProcess, setSelectedProcess] = useState<ProcessHealthDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [triggering, setTriggering] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const loadHealth = useCallback(async () => {
    try {
      setLoading(true);
      const data = await adminService.getHealth();
      setProcesses(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load health data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHealth();
  }, [loadHealth]);

  const selectProcess = async (processId: string) => {
    try {
      const detail = await adminService.getProcessHealth(processId);
      setSelectedProcess(detail);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load process details.');
    }
  };

  const runNow = async (processId: string) => {
    try {
      setTriggering(processId);
      const result = await adminService.triggerRunNow(processId);
      setError(null);
      setSuccessMessage(result.message);
      setTimeout(() => setSuccessMessage(null), 8000);
      // Reload immediately — the process already completed before response was sent
      await loadHealth();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to trigger process.');
    } finally {
      setTriggering(null);
    }
  };

  const formatDuration = (ms: number | null) => {
    if (ms === null) return '—';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const formatTime = (iso: string | null) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString();
  };

  if (loading) {
    return (
      <div >
        <h1 className="text-lg font-semibold text-text-primary mb-6">Health Dashboard</h1>
        <p>Loading health data...</p>
      </div>
    );
  }

  return (
    <div >
      <h1 className="text-lg font-semibold text-text-primary mb-2">Health Dashboard</h1>
      <p className="text-sm text-slate-500 mb-6">
        Monitor the health of all automated processes. Click a process for details.
      </p>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-6 py-4 text-red-700 text-sm mb-5" role="alert">
          {error}
          <button className="ml-4 underline" onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-xl px-6 py-4 text-green-700 text-sm mb-5" role="status">
          ✓ {successMessage}
        </div>
      )}

      {/* Status Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-6">
        {processes.map((proc) => {
          const colors = STATUS_COLORS[proc.status] ?? STATUS_COLORS.Red;
          return (
            <div
              key={proc.processId}
              className={`${colors.bg} bg-white rounded-lg border border-border p-5 cursor-pointer hover:shadow-md transition-shadow`}
              onClick={() => selectProcess(proc.processId)}
              role="button"
              tabIndex={0}
              aria-label={`${proc.processName}: ${proc.status}`}
              onKeyDown={(e) => e.key === 'Enter' && selectProcess(proc.processId)}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className={`w-3 h-3 rounded-full ${colors.dot}`} aria-hidden="true" />
                  <span className={`font-medium ${colors.text}`}>{proc.processName}</span>
                </div>
                <button
                  className="text-xs px-2.5 py-1 rounded-lg border border-border bg-white text-slate-600 hover:bg-page"
                  onClick={(e) => { e.stopPropagation(); runNow(proc.processId); }}
                  disabled={triggering === proc.processId}
                >
                  {triggering === proc.processId ? 'Running...' : 'Run Now'}
                </button>
              </div>
              <div className="text-sm text-slate-500 space-y-1">
                <div>Last run: {formatTime(proc.lastRunTime)}</div>
                <div>Next: {formatTime(proc.nextScheduledRun)}</div>
                {proc.lastStatus && (
                  <div>
                    Status: <span className={proc.lastStatus === 'success' ? 'text-green-600' : 'text-red-600'}>
                      {proc.lastStatus}
                    </span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Process Detail Panel */}
      {selectedProcess && (
        <div className="bg-white rounded-lg border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-text-primary">{selectedProcess.status.processName} — Details</h2>
            <button className="text-sm text-slate-500 hover:underline" onClick={() => setSelectedProcess(null)}>
              Close
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div>
              <div className="text-sm text-slate-500">Status</div>
              <div className={`font-medium ${STATUS_COLORS[selectedProcess.status.status]?.text}`}>
                {selectedProcess.status.status}
              </div>
            </div>
            <div>
              <div className="text-sm text-slate-500">Last Run</div>
              <div>{formatTime(selectedProcess.status.lastRunTime)}</div>
            </div>
            <div>
              <div className="text-sm text-slate-500">Records Processed</div>
              <div>{selectedProcess.status.recordsProcessed ?? '—'}</div>
            </div>
            <div>
              <div className="text-sm text-slate-500">Duration</div>
              <div>{formatDuration(selectedProcess.status.durationMs)}</div>
            </div>
            <div>
              <div className="text-sm text-slate-500">Errors</div>
              <div>{selectedProcess.status.errors ?? 0}</div>
            </div>
            <div>
              <div className="text-sm text-slate-500">Schedule</div>
              <div>Every {selectedProcess.status.scheduleMinutes} min</div>
            </div>
            <div>
              <div className="text-sm text-slate-500">Yellow Threshold</div>
              <div>{selectedProcess.status.yellowThresholdMinutes} min</div>
            </div>
            <div>
              <div className="text-sm text-slate-500">Red Threshold</div>
              <div>{selectedProcess.status.redThresholdMinutes} min</div>
            </div>
          </div>

          {/* 30-day Run History */}
          <h3 className="font-medium mb-2">30-Day Run History</h3>
          {selectedProcess.history.length === 0 ? (
            <p className="text-gray-500 text-sm">No run history in the last 30 days.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left bg-page">
                    <th className="py-2 pr-4">Time</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2 pr-4">Records</th>
                    <th className="py-2 pr-4">Errors</th>
                    <th className="py-2 pr-4">Duration</th>
                    <th className="py-2">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedProcess.history.slice(0, 50).map((entry, i) => (
                    <tr key={i} className="border-b border-border hover:bg-page/50">
                      <td className="py-2 pr-4">{formatTime(entry.timestamp)}</td>
                      <td className="py-2 pr-4">
                        <span className={entry.status === 'success' ? 'text-green-600' : 'text-red-600'}>
                          {entry.status}
                        </span>
                      </td>
                      <td className="py-2 pr-4">{entry.recordsProcessed ?? '—'}</td>
                      <td className="py-2 pr-4">{entry.errors ?? 0}</td>
                      <td className="py-2 pr-4">{formatDuration(entry.durationMs)}</td>
                      <td className="py-2 text-slate-400 truncate max-w-xs">{entry.description ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
